/*
 * Helpers shared by the three Person modules (person, person-claim,
 * person-access). They all drive the same inline-entry-row pattern the
 * Person Detail tabs are built from:
 *
 *   tr.new-<thing>-row          the hidden entry row, un-hidden and filled
 *                               with a server-rendered form
 *   tr.<thing>-row[data-id=N]   one saved row, replaced in place when edited
 *   tr.<thing>-row-validation   a validation summary row the server emits
 *                               beside a failed form
 *
 * Keeping the selectors and the busy/disabled choreography in one place is
 * what the legacy files did not do -- each of the thirteen wwwroot/js/Person
 * scripts re-derived them, and several got them wrong (see the module
 * headers for which).
 */
import { all, one } from '../core/dom';

// Re-exported so the Person modules keep importing their helpers from one
// place; the definition is in core/dom, shared with every other area.
export { escapeHtml } from '../core/dom';
import { modelStateSummary } from '../core/http';

/** Value of an element by id, '' when it is not in the page. */
export function value(id: string): string {
    const field = document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null;
    return field?.value ?? '';
}

/** Value of the first control matching a selector inside a root. */
export function valueIn(root: ParentNode | null, selector: string): string {
    const field = root?.querySelector(selector) as HTMLInputElement | HTMLSelectElement | null;
    return field?.value ?? '';
}

export function checkedIn(root: ParentNode | null, selector: string): boolean {
    return (root?.querySelector(selector) as HTMLInputElement | null)?.checked === true;
}

/** The selected option of a select inside a root (for its data-* attributes). */
export function selectedOptionIn(root: ParentNode | null, selector: string): HTMLOptionElement | null {
    const select = root?.querySelector(selector) as HTMLSelectElement | null;
    return select?.selectedOptions[0] ?? null;
}

/**
 * The Person the open detail modal belongs to. ProfileView renders the id as
 * #PersonId; every tab partial renders its own hidden with the same id, so a
 * lookup from anywhere inside the modal resolves.
 */
export function personId(): string {
    return value('PersonId');
}

/** The domain the operator is working in (rail hidden), used to scope pickers. */
export function currentDomainId(): string {
    return value('CurrentDomainId');
}

export function summaryOf(data: unknown): string {
    const modelState = (data as { modelState?: unknown } | null)?.modelState;
    return modelState ? modelStateSummary(modelState) : '';
}

/** Un-hide / hide every match by d-none -- what the row spinners run on. */
export function setHidden(selector: string, hidden: boolean): void {
    for (const node of all(selector)) node.classList.toggle('d-none', hidden);
}

/**
 * Enable / disable every match. The legacy rows put `disabled` on <span>s,
 * where it means nothing to the browser; console.css gives `[disabled]` the
 * dimmed, pointer-events-none treatment, so the attribute is what both the
 * old spans and real <button>s respond to.
 */
export function setDisabled(selector: string, disabled: boolean): void {
    for (const node of all(selector)) {
        if (disabled) node.setAttribute('disabled', 'disabled');
        else node.removeAttribute('disabled');
    }
}

export function remove(selector: string): void {
    for (const node of all(selector)) node.remove();
}


/**
 * Put an entry row back to its dormant state: drop the validation summary the
 * server may have left beside it, hide it and restore the spinner placeholder
 * so the next "add" click has something to show while its form loads.
 *
 * `columns` must match the table, or the collapsed row leaves a ragged edge --
 * every legacy copy of this used colspan="3" regardless of the table it was
 * in, which is why the Properties (5 columns) and Permissions (7 columns)
 * tables jumped when an entry form was cancelled.
 *
 * `validationClass` is omitted for an entry form whose POST answers the JSON
 * envelope rather than HTML (the role row): there is no server-rendered
 * validation sibling to clear, and naming one that no view emits is the
 * failure AjaxEntryFormWiringTests exists to catch.
 */
export function resetEntryRow(rowClass: string, validationClass: string | null, columns: number): void {
    if (validationClass) remove(`tr.${validationClass}[data-id='0']`);
    for (const row of all(`tr.${rowClass}`)) {
        row.classList.add('d-none');
        row.innerHTML = `<td colspan="${columns}"><i class="fas fa-spinner fa-spin"></i></td>`;
    }
}

/** First element for a selector, typed as HTMLElement. */
export function node(selector: string): HTMLElement | null {
    return one(selector) as HTMLElement | null;
}
